import React, { useCallback, useRef, useEffect } from 'react';
import { useInstantSearch, RefinementList } from 'react-instantsearch';
import PropTypes from 'prop-types';

// Infrastructure
import Search from '../../../../icsc-app/js/components/searches/Search';
import SearchResults from '../../../../icsc-app/js/components/searches/Search/SearchResults';
import { SAVED_SESSIONS_QUERY } from '@general/graphql/session.graph';
import { mapSearchSessionToProps } from '@general/transforms/session.transform';

// Components
import SessionListItem from '../_organisms/SessionsListItem';
import SessionUrlAutoOpener from '../_molecules/SessionsUrlAutoOpener';

// Hooks
import { useSessionsSearchFilters } from '../../hooks/useSessionsSearchFilters';

/**
 * Sub-component to sync Algolia facets with the parent's track list
 */
const TrackFacetListener = ({ setAvailableTracks, setHasLoaded }) => {
    const { results } = useInstantSearch();
    const lastTracksRef = useRef(null);
    const hasFoundFacetsRef = useRef(false);

    useEffect(() => {
        if (!results || results.__isArtificial) return;

        let facetData = results.facets?.['session_tracks'];
        if (!facetData && results.disjunctiveFacets) {
            facetData = results.disjunctiveFacets.find(f => f.attribute === 'session_tracks')?.data;
        }
        if (!facetData && results._rawResults?.[0]?.facets) {
            facetData = results._rawResults[0].facets['session_tracks'];
        }


        const hasTracks = facetData && Object.keys(facetData).length > 0;

        if (hasTracks) {
            const formattedTracks = Object.entries(facetData).map(([name, count]) => ({
                id: name,
                label: name,
                count: count
            }));

            const tracksString = JSON.stringify(formattedTracks);

            if (lastTracksRef.current !== tracksString) {
                lastTracksRef.current = tracksString;
                setAvailableTracks(formattedTracks);
            }

            hasFoundFacetsRef.current = true;
            setHasLoaded(true);
        } else {
            const timeout = setTimeout(() => {
                if (!hasFoundFacetsRef.current) {
                    setHasLoaded(true);
                }
            }, 250);

            return () => clearTimeout(timeout);
        }
    }, [results, setAvailableTracks, setHasLoaded]);

    return null;
};

/**
 * Internal component to handle conditional rendering of results
 */
const SearchContent = ({ isGlobal, sessionSlug, mapData, selectedTracks, setAvailableTracks, setHasLoaded }) => {
    const { status, results } = useInstantSearch();

    // Logic: Ready if we have results and aren't in the initial loading pulse
    const isReady = (status === 'idle' || status === 'stalled') && results && !results.__isArtificial;

    const NoResults = () => {
        const message = selectedTracks?.length > 0
            ? "There are no scheduled event sessions for your selected track(s) on this date."
            : "Coming Soon.";

        return (
            <div className="sessions-empty-state">
                {message}
            </div>
        );
    };

    return (
        <div style={{
            opacity: isReady ? 1 : 0,
            visibility: isReady ? 'visible' : 'hidden',
            transition: 'opacity 0.2s ease-in'
        }}>
            {setAvailableTracks && (
                <TrackFacetListener
                    setAvailableTracks={setAvailableTracks}
                    setHasLoaded={setHasLoaded}
                />
            )}

            {!isGlobal && (
                <>
                    <SearchResults
                        cardComponent={SessionListItem}
                        listItemComponent={SessionListItem}
                        index="event_sessions"
                        query={SAVED_SESSIONS_QUERY}
                        resultsPerRow={1}
                        transformFunc={mapData}
                        typePlural="sessions"
                        NoResultsText={NoResults}
                    />
                    <SessionUrlAutoOpener slug={sessionSlug} mapData={mapData} />
                </>
            )}
        </div>
    );
};

/**
 * SessionsSearch
 */
const SessionsSearch = ({
    activeDay,
    event,
    selectedTracks,
    setAvailableTracks,
    setHasLoaded,
    isGlobal = false,
    sessionSlug
}) => {
    const filters = useSessionsSearchFilters(event, activeDay, selectedTracks, isGlobal);

    const mapData = useCallback(hit => ({
        ...mapSearchSessionToProps(hit),
        showFavoriteIcon: true
    }), []);

    return (
        <Search
            index="event_sessions"
            displaySearchBox={false}
            displaySearchOptions={false}
            hitsPerPage={1000}
            filters={filters}
            disjunctiveFacets={['session_tracks']}
            updateSearchStateUrl={!isGlobal}
        >
            <div style={{ display: 'none' }}>
                <RefinementList attribute="session_tracks" />
            </div>

            <SearchContent
                isGlobal={isGlobal}
                sessionSlug={sessionSlug}
                mapData={mapData}
                selectedTracks={selectedTracks}
                setAvailableTracks={setAvailableTracks}
                setHasLoaded={setHasLoaded}
            />
        </Search>
    );
};

SessionsSearch.propTypes = {
    activeDay: PropTypes.instanceOf(Date),
    event: PropTypes.shape({ event_code: PropTypes.string.isRequired }),
    selectedTracks: PropTypes.arrayOf(PropTypes.string),
    setAvailableTracks: PropTypes.func,
    setHasLoaded: PropTypes.func,
    isGlobal: PropTypes.bool,
    sessionSlug: PropTypes.string,
};

export default SessionsSearch;